/* eslint-disable react-hooks/rules-of-hooks */
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { useFieldValue, useSetFieldValue } from "formik";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import * as Yup from "yup";
import { useScopeFields } from "../hooks/useScopeFields";
import { ControlState } from "../models/flexible/control-state";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { mapBasePriceKeys } from "../models/flexible/utils";
import {
	Brand,
	CoverageRequestTherapyDecision,
	Decision,
	PriceType,
	TimeframeRangeType
} from "../models/generated/graphql";
import { flexibleFormRelatedAgreements } from "../store/flexible";
import { renderRequiredMessage } from "../utils/render-required-message";
import { AdHocSectionValues } from "./ad-hoc-section-controller";
import { GeneralFieldController } from "./general-field-controller";
import { distinct } from "@lyfegen-platform/utils";

const availableOptions = [PriceType.ExFactoryPricePercent, PriceType.PublicPricePercent, PriceType.PublicPrice, PriceType.ExFactoryPrice];

interface IBasePriceEffectProps {
	section: FlexSectionController;
	currentIndex: number;
	uniqueBrandIndexes: Array<number> | undefined;
	isEditable: boolean;
}

const BasePriceEffect = (props: IBasePriceEffectProps) => {
	const discountRateSection = props.section.context.sectionControllers
		.concat(props.section.context.sectionControllers.flatMap((controller) => controller.sectionControllers || []))
		.find((controller) =>
			controller.fieldControllers.some(
				(fieldController) =>
					fieldController.schema.attribute.identifier === AttributeIdentifiers.SuggestedDiscountRate ||
					fieldController.schema.attribute.identifier === AttributeIdentifiers.DiscountRate
			)
		)?.schema.localizableKey!;
	const [discountRateSectionValue] = useFieldValue<Array<AdHocSectionValues>>(discountRateSection);
	const setFieldValue = useSetFieldValue();
	const { currentIndex, isEditable, uniqueBrandIndexes } = props;
	const parentIndex = uniqueBrandIndexes?.reduce((accumulator, current) => (currentIndex >= current ? current : accumulator), 0) || 0;
	const discountRateValue = discountRateSectionValue[currentIndex];
	const parentDiscountRateValue = discountRateSectionValue[parentIndex];
	const ref = useRef(discountRateValue.basePrice);

	useEffect(() => {
		if (
			isEditable &&
			ref.current !== discountRateValue.basePrice &&
			uniqueBrandIndexes?.length &&
			parentIndex === currentIndex &&
			(discountRateValue.suggestedDiscountRate || discountRateValue.fixedFeeAmount || discountRateValue.discountRate)
		) {
			ref.current = discountRateValue.basePrice;
			setFieldValue(`${discountRateSection}.${currentIndex}`, {
				...discountRateValue,
				suggestedDiscountRate: null,
				discountRate: null,
				fixedFeeAmount: null,
			});
		}
	}, [discountRateValue.basePrice]);

	useEffect(() => {
		if (
			isEditable &&
			uniqueBrandIndexes?.length &&
			parentIndex !== currentIndex &&
			parentDiscountRateValue.basePrice !== discountRateValue.basePrice
		) {
			setFieldValue(`${discountRateSection}.${currentIndex}`, {
				...discountRateValue,
				basePrice: parentDiscountRateValue.basePrice,
				suggestedDiscountRate: null,
				discountRate: null,
				fixedFeeAmount: null,
			});
		}
	}, [parentDiscountRateValue.basePrice, parentIndex, currentIndex]);

	return null;
};

export class BasePriceFieldController extends GeneralFieldController {
	public currentIndex = 0;

	override useControlState(path: string): ControlState {
		const index = +(path.split(".").find((e) => Number.isInteger(+e)) || 0);
		const agreementsData = useRecoilValue(flexibleFormRelatedAgreements);
		const [decisionTherapy] = useFieldValue<Decision>(AttributeIdentifiers.DecisionTherapy);
		if (decisionTherapy == null) {
			return "hidden";
		}

		if (this.parent.context.uniqueBrandIndexes && !this.parent.context.uniqueBrandIndexes.includes(index)) return "empty";

		if (
			this.parent.context.isNegotiable &&
			agreementsData?.data?.every(({ agreement }) =>
				agreement.negotiableAttributes.every((attribute) => attribute.identifier !== AttributeIdentifiers.BasePrice)
			)
		) {
			return "disabled";
		}

		return "enabled";
	}

	override useControlProps(path: string) {
		const { t } = useTranslation();
		const agreementsData = useRecoilValue(flexibleFormRelatedAgreements);
		const { multiBrandSets } = useScopeFields(this.parent.context);
		const [decisionSectionValue] = useFieldValue(this.parent.path);
		this.currentIndex = +(path.split(".").find((e) => Number.isInteger(+e)) || 0);
		const [decisionTherapy] = useFieldValue<Decision>(AttributeIdentifiers.DecisionTherapy);

		const priceTypeMap = new Map();
		const hasMultipleBasePrices = agreementsData.allMatchedAgreementVersions?.some((agreement) =>
			agreement.scenarios.some((scenario) => !!scenario.hasMultipleBasePrices)
		);

		const allActions =
			agreementsData.allMatchedAgreementVersions?.flatMap((agreement) =>
				agreement.scenarios.flatMap((scenario) =>
					scenario.actions.map((action) => ({ action, timeframe: action.timeframeRangeType }))
				)
			) || [];

		allActions.forEach(({ action, timeframe }) => {
			if (
				!action.brandsConnection.edges.some(
					(edge) => edge.node.Name === decisionSectionValue[this.currentIndex][AttributeIdentifiers.BrandNameReference].Name
				)
			) {
				return;
			}

			const priceTypes =
				action.priceType && (!action.adHocPriceTypes || !action.adHocPriceTypes.length)
					? [action.priceType]
					: action.adHocPriceTypes || [];

			const filteredPriceTypes = priceTypes.filter((priceType) => availableOptions.includes(priceType));

			if (filteredPriceTypes.length > 0) {
				if (!priceTypeMap.has(timeframe)) {
					priceTypeMap.set(timeframe, []);
				}
				priceTypeMap.get(timeframe).push(...filteredPriceTypes);
			}
		});

		function filterBasePriceOptions(actionMap: Map<TimeframeRangeType, PriceType[]>) {
			const getDistinctOrDefault = (key: TimeframeRangeType, defaultValue: PriceType[]) => {
				const actions = actionMap.get(key);
				return actions ? distinct(actions) : defaultValue;
			};

			if (hasMultipleBasePrices) {
				switch (decisionTherapy) {
					case CoverageRequestTherapyDecision.Trial:
						return getDistinctOrDefault(TimeframeRangeType.WithinTherapyTrial, [PriceType.ExFactoryPricePercent]);
					case CoverageRequestTherapyDecision.Approved:
						return getDistinctOrDefault(TimeframeRangeType.WithinTheCoveragePeriod, [PriceType.ExFactoryPricePercent]);
					default:
						return [PriceType.ExFactoryPricePercent];
				}
			}

			const combinedActions = distinct(Array.from(actionMap.values()).flatMap((actions) => actions));
			return combinedActions.length ? combinedActions : availableOptions;
		}

		const optionToShow = filterBasePriceOptions(priceTypeMap);
		const options = optionToShow.map((priceType) => ({
			label: t(mapBasePriceKeys(priceType)),
			value: priceType,
		}));

		return {
			controlType: "typeahead",
			options,
			disabled: options.length === 1,
			property: "value",
			selectPropertyValue: true,
			emptyValuePlaceholder: "n/a",
			required: multiBrandSets.some((multiBrandSet) =>
				multiBrandSet.some(
					(brand) => brand.Name === decisionSectionValue[this.currentIndex][AttributeIdentifiers.BrandNameReference]?.Name
				)
			),
		};
	}

	override createValidationRule(_initialData: unknown, translation: (key: string) => string): Yup.AnySchema {
		return Yup.string()
			.nullable()
			.when(["$mergedFormValues", AttributeIdentifiers.BrandNameReference], ((
				values: any,
				brandNameReference: Brand,
				schema: Yup.StringSchema
			) => {
				return values?.preparations.some((preparation: any) =>
					preparation[AttributeIdentifiers.BrandName].some((brand: Brand) => brand.Name === brandNameReference.Name)
				)
					? schema.required(renderRequiredMessage(this.schema.attribute.identifier, translation))
					: schema;
			}) as any);
	}

	renderAfter(path: string): React.ReactElement {
		const index = +(path.split(".").find((e) => Number.isInteger(+e)) || 0);
		return (
			<BasePriceEffect
				section={this.parent}
				currentIndex={index}
				uniqueBrandIndexes={this.parent.context.uniqueBrandIndexes}
				isEditable={this.parent.context.isEditable}
			/>
		);
	}
}
